import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { API_INSTANCE, buildURL } from 'src/features/utils';
import { useSelector } from 'react-redux';
import { selectSiteData } from 'src/features/account-slice';
import { OverlayPanel } from 'primereact/overlaypanel';
import { classes } from './pin-verification-modal.st.css';
import { Box, Loader } from 'wix-style-react';

const VerificationContent = ({
  email,
  disabled,
  isCodeVerified,
  verificationInitiated,
  verificationCodeInput,
  onCodeChange,
  sendCode,
  verifyCode,
  includeCaptcha,
  buttonVerifyButtonText,
  verificationStatus,
  className,
  recaptchaRef,
  closeDialog,
  isAutoOpen,
}) => {
  const captchaKey = typeof process === 'undefined' ? null : process?.env?.RECAPTCHA_CAPTCHA_KEY;
  return (
    <div className="flex flex-column space-y-4">
      {!isCodeVerified && (
        <p className="text-sm text-gray-600">
          Please enter the 6-character code sent to your email: <strong>{email}</strong>
        </p>
      )}
      <div className="flex space-x-2 flex-column gap-3">
        {!verificationInitiated && !isCodeVerified && (
          <div className="flex items-center space-x-2">
            <InputText
              value={verificationCodeInput}
              placeholder="Enter 6-character code"
              maxLength={6}
              onChange={onCodeChange}
              className="border rounded-md px-3 py-2 flex-1"
              autoFocus
            />
            <Button label="Resend" onClick={sendCode} className="py-2 px-4 rounded-md" outlined />
          </div>
        )}
        {includeCaptcha && captchaKey && (
          <div>
            <ReCAPTCHA ref={recaptchaRef} size="normal" sitekey={captchaKey} />
          </div>
        )}
        {!isCodeVerified && (
          <div className="text-center">
            <Button
              label={buttonVerifyButtonText}
              onClick={verifyCode}
              className={`${className} self-end py-2 px-4 rounded-md mb-2`}
              size="large"
            />
          </div>
        )}
      </div>
      {verificationStatus && (
        <small
          className={`font-medium mb-2 mt-2 text-base ${
            isCodeVerified ? 'text-green-700' : 'text-red-700'
          }`}
        >
          {verificationStatus}
        </small>
      )}
      {isCodeVerified && !isAutoOpen && (
        <div className="text-center">
          <Button label="Close" onClick={closeDialog} className={`mt-2 py-2 px-4 rounded-md`} />
        </div>
      )}
      {isCodeVerified && isAutoOpen && (
        <div className="text-center">
          <Loader size="medium" />
        </div>
      )}
    </div>
  );
};

export const PinVerificationModal = ({
  autoOpen = false,
  disabled = false,
  includeCaptcha,
  validate,
  validationFailedText = 'Please fill in all required fields',
  className,
  codeSuccesfulVerificationUrl,
  buttonText,
  buttonVerifyButtonText = 'Verify Code',
  pinVerifiedCallback,
  email,
}) => {
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [validationFailed, setValidationFailedText] = useState(null);
  const [verificationInitiated, setVerificationInitiated] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [codeID, setCodeID] = useState(null);
  const CODE_LENGTH = 6;
  const site = useSelector(selectSiteData);
  const recaptchaRef = useRef(null);
  const verifyOverlay = useRef(null);

  const closeDialog = (e) => {
    setVerificationInitiated(false);
    setVerificationCodeInput('');
    setVerificationStatus('');
    setIsCodeVerified(false);
    if (!autoOpen) {
      // Only toggle if not autoOpen
      verifyOverlay.current.toggle(e);
    }
  };

  useEffect(() => {
    if (autoOpen) {
      sendCode();
    }
  }, [autoOpen]);

  const sendCode = async () => {
    try {
      setVerificationStatus('');
      const { data } = await API_INSTANCE.post(buildURL(site, `/api/v1/auth/code-sender`), {
        email,
      });
      setCodeID(data.codeId);
      setVerificationInitiated(true);
    } catch (err) {
      console.error(err);
      setVerificationStatus('Failed to send code. Try again later!');
    }
  };

  const onCodeChange = (e) => {
    const value = e.target.value;
    setVerificationCodeInput(value);
  };

  const verifyCode = async (e) => {
    setVerificationStatus('');

    if (verificationCodeInput.length === CODE_LENGTH) {
      const captchaToken = includeCaptcha ? recaptchaRef.current.getValue() : null;

      try {
        const { data } = await API_INSTANCE.post(buildURL(site, codeSuccesfulVerificationUrl), {
          email,
          captchaToken,
          code_verification: { code: verificationCodeInput, id: codeID },
        });

        setIsCodeVerified(true);
        pinVerifiedCallback(e, data, captchaToken);
      } catch (err) {
        console.error(err);
        setVerificationStatus('Code is invalid. Try again!');
      }
    } else {
      setVerificationStatus(`Code should be at least ${CODE_LENGTH} characters long!`);
    }
  };

  return (
    <>
      {!autoOpen && (
        <Box direction="vertical" gap="2" marginBottom={5}>
          <Button
            disabled={disabled}
            className="m-0"
            onClick={(e) => {
              if (validate && validate()) {
                setValidationFailedText(null);
                verifyOverlay.current.toggle(e);
                sendCode();
              } else {
                setValidationFailedText(validationFailedText);
              }
            }}
          >
            {buttonText}
          </Button>
          {validationFailed && <small className="text-red-700">{validationFailedText}</small>}
        </Box>
      )}
      {autoOpen ? (
        <VerificationContent
          email={email}
          disabled={disabled}
          isCodeVerified={isCodeVerified}
          verificationInitiated={verificationInitiated}
          verificationCodeInput={verificationCodeInput}
          onCodeChange={onCodeChange}
          sendCode={sendCode}
          verifyCode={verifyCode}
          includeCaptcha={includeCaptcha}
          buttonVerifyButtonText={buttonVerifyButtonText}
          verificationStatus={verificationStatus}
          className={className}
          recaptchaRef={recaptchaRef}
          closeDialog={closeDialog}
          isAutoOpen={true}
        />
      ) : (
        <OverlayPanel appendTo={document.body} ref={verifyOverlay} className={classes.overlay}>
          <VerificationContent
            email={email}
            disabled={disabled}
            isCodeVerified={isCodeVerified}
            verificationInitiated={verificationInitiated}
            verificationCodeInput={verificationCodeInput}
            onCodeChange={onCodeChange}
            sendCode={sendCode}
            verifyCode={verifyCode}
            includeCaptcha={includeCaptcha}
            buttonVerifyButtonText={buttonVerifyButtonText}
            verificationStatus={verificationStatus}
            className={className}
            recaptchaRef={recaptchaRef}
            closeDialog={closeDialog}
            isAutoOpen={false}
          />
        </OverlayPanel>
      )}
    </>
  );
};
